import { useState, useEffect, useRef } from 'react'
import {
  Search,
  UserPlus,
  Loader2,
  Check,
  Calendar as CalendarIcon,
  Clock,
  AlertTriangle,
  Info,
} from 'lucide-react'
import { Button } from '@components/ui/button'
import { Input } from '@components/ui/input'
import { Textarea } from '@components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@components/ui/tooltip'
import { useToast } from '@hooks/use-toast'
import { useAuthStore } from '@hooks/useAuthStore'
import { usePatients, useCreatePatient } from '@hooks/usePatients'
import { appointmentApi } from '@services/appointment.service'
import { formatClinicDate, formatClinicTime } from '@lib/clinicDate'
import { cn } from '@lib/utils'
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover'
import { Calendar } from '@components/ui/calendar'
import { DoctorAvatar, guessGender } from './DoctorAvatar'
import { Patient, AppointmentCreate, Gender, AllergySeverity } from '@/types'
import { useQuery, useQueryClient } from '@tanstack/react-query'

interface CreateAppointmentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

// The backend computes all availability in Mexico City time, so the date
// picker must reckon "today" and the bookable window in the same timezone,
// not the browser's local timezone.
const CLINIC_TZ = 'America/Mexico_City'

// Teal is this dialog's institutional accent. Feed it to the calendar so the
// selected day matches the primary "Agendar Cita" button instead of the
// app-wide green default.
const TEAL_RGB = '13 148 136' // tailwind teal-600
const calendarAccent = {
  '--rdp-accent-color': `rgb(${TEAL_RGB})`,
  '--rdp-accent-background-color': `rgb(${TEAL_RGB} / 0.14)`,
} as React.CSSProperties

const GENDER_LABELS: Record<Gender, string> = {
  male: 'Masculino',
  female: 'Femenino',
  non_binary: 'No binario',
  prefer_not_to_say: 'No especificado',
  other: 'Otro',
}

const TYPE_LABELS: Record<string, string> = {
  consultation: 'Valoración',
  follow_up: 'Seguimiento',
  emergency: 'Urgencia',
  routine_checkup: 'Revisión',
  procedure: 'Tratamiento',
}

// Catálogo dental: cada tratamiento fija tipo, duración y motivo por defecto.
// La duración manda sobre el slot del doctor (una endodoncia no dura 30 min).
export const DENTAL_TREATMENTS: { key: string; label: string; type: string; minutes: number }[] = [
  { key: 'valoracion',    label: 'Valoración / primera vez',   type: 'consultation',     minutes: 30 },
  { key: 'limpieza',      label: 'Limpieza dental',            type: 'procedure',        minutes: 45 },
  { key: 'resina',        label: 'Resina / empaste',           type: 'procedure',        minutes: 45 },
  { key: 'blanqueamiento',label: 'Blanqueamiento',             type: 'procedure',        minutes: 60 },
  { key: 'extraccion',    label: 'Extracción',                 type: 'procedure',        minutes: 60 },
  { key: 'endodoncia',    label: 'Endodoncia',                 type: 'procedure',        minutes: 90 },
  { key: 'corona',        label: 'Corona / carilla',           type: 'procedure',        minutes: 60 },
  { key: 'ortodoncia',    label: 'Ajuste de ortodoncia',       type: 'follow_up',        minutes: 20 },
  { key: 'revision',      label: 'Revisión / seguimiento',     type: 'routine_checkup',  minutes: 30 },
  { key: 'urgencia',      label: 'Urgencia por dolor',         type: 'emergency',        minutes: 30 },
]

const ALLERGY_SEVERITY_STYLES: Record<AllergySeverity, string> = {
  severe: 'bg-red-100 text-red-800 border-red-200',
  moderate: 'bg-amber-100 text-amber-800 border-amber-200',
  mild: 'bg-yellow-50 text-yellow-800 border-yellow-200',
}

// Compact control sizing shared across every field so the whole form fits one
// screen without scrolling: ~36px tall (h-9), 13px value text.
const COMPACT_FIELD = 'h-9 text-[13px]'

// Returns the current date in the clinic timezone as YYYY-MM-DD.
function clinicToday(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: CLINIC_TZ }).format(new Date())
}

// Adds `days` to a YYYY-MM-DD string without timezone drift.
function addDays(ymd: string, days: number): string {
  const [y, m, d] = ymd.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d))
  dt.setUTCDate(dt.getUTCDate() + days)
  return dt.toISOString().slice(0, 10)
}

// A discreet 12px field label. Long helper text is replaced by an optional
// hover tooltip (`hint`) so explanations don't consume vertical space.
function FieldLabel({ children, hint }: { children: React.ReactNode; hint?: string }) {
  return (
    <div className="mb-1 flex items-center gap-1">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{children}</span>
      {hint && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button type="button" className="text-slate-400 hover:text-slate-600" tabIndex={-1} aria-label="Más información">
              <Info className="h-3 w-3" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[220px] text-xs">{hint}</TooltipContent>
        </Tooltip>
      )}
    </div>
  )
}

export function CreateAppointmentDialog({ open, onOpenChange, onSuccess }: CreateAppointmentDialogProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const currentUser = useAuthStore((s) => s.user)

  // Patient selection
  const [patientSearch, setPatientSearch] = useState('')
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null)
  const [showDropdown, setShowDropdown] = useState(false)
  const [showInlineCreate, setShowInlineCreate] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Inline patient creation
  const [newFirstName, setNewFirstName] = useState('')
  const [newLastName, setNewLastName] = useState('')
  const [newPhone, setNewPhone] = useState('')
  const [newBirthDate, setNewBirthDate] = useState('')
  const [emergencyName, setEmergencyName] = useState('')
  const [emergencyRelationship, setEmergencyRelationship] = useState('')
  const [emergencyPhone, setEmergencyPhone] = useState('')
  const canCreatePatient = !!(newFirstName.trim() && newLastName.trim() && newBirthDate
    && newBirthDate <= clinicToday() && emergencyName.trim() && emergencyRelationship.trim()
    && emergencyPhone.trim().length >= 5)
  const { mutateAsync: createPatient, isPending: isCreatingPatient } = useCreatePatient()

  // Doctor selection. The doctor list is cached (react-query) so reopening the
  // booking dialog is instant instead of refetching every time.
  const [doctorId, setDoctorId] = useState('')
  const { data: doctorsData } = useQuery({
    queryKey: ['appointment-doctors'],
    queryFn: () => appointmentApi.getDoctors(),
    staleTime: 5 * 60_000,
    enabled: open,
  })
  const doctors = doctorsData?.items ?? []
  const [maxHorizonDays, setMaxHorizonDays] = useState(30)

  // Appointment form
  const [availableSlots, setAvailableSlots] = useState<{ start_at: string; end_at: string }[]>([])
  const [slotsTimezone, setSlotsTimezone] = useState('UTC')
  const [slotsLoading, setSlotsLoading] = useState(false)
  const [slotsError, setSlotsError] = useState<string | null>(null)
  const [selectedDate, setSelectedDate] = useState('') // YYYY-MM-DD in clinic tz
  const [appointmentDate, setAppointmentDate] = useState('')
  const [durationMinutes, setDurationMinutes] = useState(30)
  const [appointmentType, setAppointmentType] = useState('consultation')
  const [treatment, setTreatment] = useState('')
  const requestedDuration = treatment ? durationMinutes : undefined
  const [reason, setReason] = useState('')
  const [notes, setNotes] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  // Turns on inline field validation only after the first save attempt, so the
  // form doesn't scold the user before they've done anything.
  const [attemptedSubmit, setAttemptedSubmit] = useState(false)

  // Patient search query
  const { data: patientsData } = usePatients(
    patientSearch.length >= 2 ? { query: patientSearch, limit: 5 } : undefined
  )
  const searchResults = patientSearch.length >= 2 ? (patientsData?.items || []) : []

  // Pick a default doctor as soon as the (cached) list is available.
  useEffect(() => {
    const items = doctorsData?.items ?? []
    if (!open || items.length === 0) return
    setDoctorId((prev) => {
      if (prev) return prev
      if (currentUser?.role === 'doctor' && currentUser.id) return currentUser.id
      return items[0]?.id ?? ''
    })
  }, [open, doctorsData, currentUser?.id, currentUser?.role])

  // Load the booking horizon and the open slots in a single pass when the dialog
  // opens or the doctor changes. Previously the horizon and slots were separate
  // effects, which fetched the slot list twice on every open.
  useEffect(() => {
    if (!open || !doctorId) return
    let cancelled = false
    ;(async () => {
      setSlotsLoading(true)
      setSlotsError(null)
      setAppointmentDate('')
      setAvailableSlots([])
      try {
        const rule = await appointmentApi.getAvailabilityRule(doctorId).catch(() => null)
        const horizon = rule?.max_horizon_days || 30
        if (cancelled) return
        setMaxHorizonDays(horizon)
        const today = clinicToday()
        const response = await appointmentApi.getAvailableSlots(
          doctorId,
          today,
          addDays(today, horizon),
          undefined,
          requestedDuration,
        )
        if (cancelled) return
        setAvailableSlots(response.slots)
        setSlotsTimezone(response.timezone || 'UTC')
        if (requestedDuration === undefined) setDurationMinutes(response.slot_minutes)
        // Keep the chosen slot only if it's still open.
        setAppointmentDate((prev) =>
          prev && response.slots.some((s) => s.start_at === prev) ? prev : '',
        )
      } catch (err: any) {
        if (!cancelled) {
          setAvailableSlots([])
          setSlotsError(
            err?.response?.data?.detail || 'No se pudieron cargar los horarios disponibles',
          )
        }
      } finally {
        if (!cancelled) setSlotsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, doctorId, requestedDuration])

  // Default the Fecha box to the first day that has availability.
  useEffect(() => {
    if (!availableSlots.length) {
      setSelectedDate('')
      return
    }
    const days = availableSlots.map((s) =>
      new Intl.DateTimeFormat('en-CA', { timeZone: slotsTimezone }).format(new Date(s.start_at)),
    )
    setSelectedDate((prev) => (prev && days.includes(prev) ? prev : days[0]))
  }, [availableSlots, slotsTimezone])

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const handleCreateInlinePatient = async () => {
    if (!canCreatePatient || isCreatingPatient) return
    try {
      const patient = await createPatient({
        first_name: newFirstName.trim(),
        last_name: newLastName.trim(),
        phone_primary: newPhone.trim() || undefined,
        date_of_birth: newBirthDate,
        gender: 'prefer_not_to_say',
        emergency_contacts: [
          {
            name: emergencyName.trim(),
            relationship: emergencyRelationship.trim(),
            phone: emergencyPhone.trim(),
            is_primary: true,
          },
        ],
      })
      setSelectedPatient(patient)
      setShowInlineCreate(false)
      setPatientSearch(`${patient.first_name} ${patient.last_name}`)
      setShowDropdown(false)
      setNewFirstName('')
      setNewLastName('')
      setNewPhone('')
      setNewBirthDate('')
      setEmergencyName('')
      setEmergencyRelationship('')
      setEmergencyPhone('')
      toast({ title: 'Paciente creado', description: `${patient.first_name} ${patient.last_name} registrado.` })
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err?.message || 'No se pudo crear el paciente',
        variant: 'destructive',
      })
    }
  }

  // Field-level validation for the required inputs. Surfaced inline (not via a
  // browser alert) once the user has attempted to save.
  const errors = {
    patient: !selectedPatient,
    date: !selectedDate,
    time: !appointmentDate,
    reason: !reason.trim(),
  }
  const hasErrors = errors.patient || errors.date || errors.time || errors.reason || slotsLoading || !!slotsError

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (hasErrors) {
      setAttemptedSubmit(true)
      return
    }

    setIsSubmitting(true)
    try {
      const data: AppointmentCreate = {
        patient_id: selectedPatient!.id,
        doctor_id: doctorId,
        appointment_date: appointmentDate,
        duration_minutes: durationMinutes,
        appointment_type: appointmentType,
        reason: reason.trim(),
        notes: notes.trim() || undefined,
      }
      await appointmentApi.createAppointment(data)
      toast({
        title: 'Cita creada',
        description: `${formatClinicDate(appointmentDate, slotsTimezone)} · ${formatClinicTime(appointmentDate, slotsTimezone)}`,
      })
      queryClient.invalidateQueries({ queryKey: ['appointments'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      onSuccess?.()
      resetAndClose()
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err?.response?.data?.detail || 'No se pudo crear la cita',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const resetAndClose = () => {
    setSelectedPatient(null)
    setPatientSearch('')
    setShowDropdown(false)
    setShowInlineCreate(false)
    setNewFirstName('')
    setNewLastName('')
    setNewPhone('')
    setNewBirthDate('')
    setEmergencyName('')
    setEmergencyRelationship('')
    setEmergencyPhone('')
    setDoctorId('')
    setAvailableSlots([])
    setSelectedDate('')
    setAppointmentDate('')
    setAppointmentType('consultation')
    setTreatment('')
    setReason('')
    setNotes('')
    setAttemptedSubmit(false)
    onOpenChange(false)
  }

  // Bookable date window: today through today + horizon, in clinic timezone
  const minDateStr = clinicToday()
  const maxDateStr = addDays(minDateStr, maxHorizonDays)
  // Date format follows the doctor's clinical-availability region (returned by
  // the slots API as slotsTimezone).
  const maxDateLabel = formatClinicDate(`${maxDateStr}T12:00:00`, slotsTimezone)
  // The last open slot is the actual date and time up to which appointments can
  // be booked (availability may run out before the horizon limit).
  const lastSlot = availableSlots[availableSlots.length - 1]
  const availableUntilLabel = lastSlot
    ? `${formatClinicDate(lastSlot.start_at, slotsTimezone)} ${formatClinicTime(lastSlot.start_at, slotsTimezone)}`
    : maxDateLabel

  // The "Fecha" box lists each day that has availability; the "Horario" box lists
  // the times for the chosen day. Both derive from the loaded slots so they stay
  // in the clinic timezone.
  const clinicDay = (iso: string) =>
    new Intl.DateTimeFormat('en-CA', { timeZone: slotsTimezone }).format(new Date(iso))
  const availableDates: { date: string; label: string }[] = []
  const seenDates = new Set<string>()
  for (const slot of availableSlots) {
    const day = clinicDay(slot.start_at)
    if (!seenDates.has(day)) {
      seenDates.add(day)
      availableDates.push({ date: day, label: formatClinicDate(slot.start_at, slotsTimezone) })
    }
  }
  const slotsForSelectedDate = availableSlots.filter((s) => clinicDay(s.start_at) === selectedDate)

  // Helpers to bridge the YYYY-MM-DD day keys with the calendar's Date objects.
  const parseYmd = (ymd: string) => {
    const [y, m, d] = ymd.split('-').map(Number)
    return new Date(y, m - 1, d)
  }
  const toYmd = (date: Date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
  const availableDateSet = new Set(availableDates.map((d) => d.date))
  const selectedDateLabel = availableDates.find((d) => d.date === selectedDate)?.label ?? ''

  const noAvailability = !slotsError && !slotsLoading && availableSlots.length === 0
  const selectedDoctor = doctors.find((d) => d.id === doctorId)
  const selectedDoctorGender = selectedDoctor ? guessGender(selectedDoctor.first_name) : 'male'
  const summaryReady = !!(selectedPatient && selectedDate && appointmentDate)

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetAndClose(); else onOpenChange(true) }}>
      {/* Width 760px fits two columns; height is capped to the viewport minus a
          40px top/bottom margin. Desktop content fits inside with no scroll;
          overflow-y-auto only kicks in on short/mobile screens. */}
      <DialogContent className="flex max-h-[calc(100dvh-80px)] w-[95vw] max-w-[760px] flex-col gap-0 overflow-hidden rounded-2xl border-teal-100 p-0">
        <TooltipProvider delayDuration={150}>
          <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
            {/* Compact single-line header */}
            <div className="flex shrink-0 items-center gap-3 border-b border-teal-100 px-6 py-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-teal-100 text-teal-700">
                <CalendarIcon className="h-[18px] w-[18px]" />
              </span>
              <div className="min-w-0">
                <DialogTitle className="font-headline text-xl italic leading-none text-ml-primary">
                  Nueva Cita
                </DialogTitle>
                <DialogDescription className="mt-0.5 text-[11px] leading-none text-slate-500">Complete los detalles para agendar.</DialogDescription>
              </div>
            </div>

            {/* Body — two columns, no scroll on desktop */}
            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4 [@media(max-height:740px)]:py-2.5">
              <div className="grid grid-cols-1 gap-x-5 gap-y-3 md:grid-cols-2 [@media(max-height:740px)]:gap-y-2">
                {/* ============ LEFT: patient + type + reason + notes ============ */}
                <div className="space-y-2.5 [@media(max-height:740px)]:space-y-2">
                  {/* Paciente */}
                  <div>
                    <FieldLabel>Paciente *</FieldLabel>
                    {selectedPatient ? (
                      <PatientMiniProfile
                        patient={selectedPatient}
                        onChange={() => {
                          setSelectedPatient(null)
                          setPatientSearch('')
                        }}
                      />
                    ) : (
                      <div className="relative" ref={dropdownRef}>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            placeholder="Buscar por nombre o expediente..."
                            className={cn('pl-9', COMPACT_FIELD, attemptedSubmit && errors.patient && 'border-red-400 focus-visible:ring-red-400')}
                            value={patientSearch}
                            onChange={(e) => {
                              setPatientSearch(e.target.value)
                              setShowDropdown(true)
                              setShowInlineCreate(false)
                            }}
                            onFocus={() => setShowDropdown(true)}
                          />
                        </div>

                        {showDropdown && (patientSearch.length >= 2 || showInlineCreate) && (
                          <div className={cn('z-50 w-full mt-1 bg-card border rounded-lg shadow-lg', showInlineCreate ? 'relative' : 'absolute max-h-56 overflow-y-auto')}>
                            {searchResults.map((patient) => (
                              <button
                                key={patient.id}
                                type="button"
                                className="w-full flex items-center gap-3 p-2.5 hover:bg-muted/50 transition-colors text-left"
                                onClick={() => {
                                  setSelectedPatient(patient)
                                  setPatientSearch(`${patient.first_name} ${patient.last_name}`)
                                  setShowDropdown(false)
                                }}
                              >
                                <div className="h-8 w-8 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 font-semibold text-xs shrink-0">
                                  {patient.first_name[0]}{patient.last_name[0]}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-[13px] font-medium truncate">
                                    {patient.first_name} {patient.last_name}
                                  </p>
                                  <p className="text-[11px] text-muted-foreground">
                                    {patient.mrn}
                                    {patient.phone_primary ? ` · ${patient.phone_primary}` : ''}
                                  </p>
                                </div>
                              </button>
                            ))}

                            {patientSearch.length >= 2 && searchResults.length === 0 && !showInlineCreate && (
                              <p className="p-3 text-sm text-muted-foreground text-center">
                                No se encontraron pacientes
                              </p>
                            )}

                            {/* Inline create — a discreet text link, not a button */}
                            {!showInlineCreate ? (
                              <button
                                type="button"
                                className="w-full flex items-center gap-2 px-3 py-2 border-t text-left text-[13px] text-teal-700 underline-offset-2 hover:underline"
                                onClick={() => setShowInlineCreate(true)}
                              >
                                <UserPlus className="h-3.5 w-3.5" />
                                <span>Crear nuevo paciente</span>
                              </button>
                            ) : (
                              <div className="p-3 border-t space-y-2.5 bg-muted/30">
                                <p className="text-[13px] font-medium text-foreground">Nuevo Paciente</p>
                                <div className="grid grid-cols-2 gap-2">
                                  <Input
                                    placeholder="Nombre"
                                    className={COMPACT_FIELD}
                                    value={newFirstName}
                                    onChange={(e) => setNewFirstName(e.target.value)}
                                    autoFocus
                                  />
                                  <Input
                                    placeholder="Apellido"
                                    className={COMPACT_FIELD}
                                    value={newLastName}
                                    onChange={(e) => setNewLastName(e.target.value)}
                                  />
                                </div>
                                <Input
                                  placeholder="Teléfono"
                                  className={COMPACT_FIELD}
                                  value={newPhone}
                                  onChange={(e) => setNewPhone(e.target.value)}
                                />
                                <p className="text-xs text-muted-foreground">Completa los datos reales para abrir el expediente. Si falta alguno, consúltalo con el paciente.</p>
                                <label className="block text-xs">
                                  Fecha de nacimiento
                                  <Input type="date" aria-label="Fecha de nacimiento" max={clinicToday()}
                                    className={COMPACT_FIELD} value={newBirthDate}
                                    onChange={(e) => setNewBirthDate(e.target.value)} />
                                </label>
                                <Input aria-label="Contacto de emergencia" placeholder="Contacto de emergencia"
                                  maxLength={100} className={COMPACT_FIELD} value={emergencyName}
                                  onChange={(e) => setEmergencyName(e.target.value)} />
                                <Input aria-label="Parentesco" placeholder="Parentesco"
                                  maxLength={50} className={COMPACT_FIELD} value={emergencyRelationship}
                                  onChange={(e) => setEmergencyRelationship(e.target.value)} />
                                <Input aria-label="Teléfono de emergencia" placeholder="Teléfono de emergencia"
                                  type="tel" maxLength={20} className={COMPACT_FIELD} value={emergencyPhone}
                                  onChange={(e) => setEmergencyPhone(e.target.value)} />
                                <div className="flex gap-2">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setShowInlineCreate(false)}
                                  >
                                    Cancelar
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    className="bg-teal-600 hover:bg-teal-700 text-white"
                                    disabled={!canCreatePatient || isCreatingPatient}
                                    onClick={handleCreateInlinePatient}
                                  >
                                    {isCreatingPatient ? (
                                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                    ) : (
                                      <Check className="h-4 w-4 mr-1" />
                                    )}
                                    Crear
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    {attemptedSubmit && errors.patient && <FieldError>Selecciona un paciente.</FieldError>}
                  </div>

                  {/* Tipo de cita */}
                  <div>
                    <FieldLabel hint="Fija tipo, duración y motivo por defecto. Puedes editar el motivo.">Tratamiento</FieldLabel>
                    <Select
                      value={treatment}
                      onValueChange={(key) => {
                        const tr = DENTAL_TREATMENTS.find((x) => x.key === key)
                        if (!tr) return
                        setTreatment(key)
                        setAppointmentType(tr.type)
                        setDurationMinutes(tr.minutes)
                        setReason((prev) => (prev.trim() === '' || DENTAL_TREATMENTS.some((x) => x.label === prev) ? tr.label : prev))
                      }}
                    >
                      <SelectTrigger className={COMPACT_FIELD}>
                        <SelectValue placeholder="Elige el tratamiento" />
                      </SelectTrigger>
                      <SelectContent>
                        {DENTAL_TREATMENTS.map((tr) => (
                          <SelectItem key={tr.key} value={tr.key}>{tr.label} · {tr.minutes} min</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Motivo */}
                  <div>
                    <FieldLabel hint="Motivo principal de la consulta (máx. 500 caracteres).">Motivo *</FieldLabel>
                    <Input
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Motivo de la consulta"
                      className={cn(COMPACT_FIELD, attemptedSubmit && errors.reason && 'border-red-400 focus-visible:ring-red-400')}
                    />
                    {attemptedSubmit && errors.reason && <FieldError>Indica el motivo.</FieldError>}
                  </div>

                  {/* Notas */}
                  <div>
                    <FieldLabel hint="Información adicional para el doctor. Opcional.">Notas</FieldLabel>
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Notas adicionales..."
                      rows={2}
                      className="min-h-0 resize-none py-1.5 text-[13px]"
                    />
                  </div>
                </div>

                {/* ============ RIGHT: doctor + date/time + duration/status ============ */}
                <div className="space-y-2.5 [@media(max-height:740px)]:space-y-2">
                  {/* Doctor */}
                  <div>
                    <FieldLabel>Doctor asignado</FieldLabel>
                    {doctors.length > 1 ? (
                      <Select
                        value={doctorId}
                        onValueChange={(v) => {
                          // Radix fires onValueChange('') while items are still
                          // registering when set programmatically; ignore it so the
                          // auto-selected doctor isn't wiped.
                          if (!v) return
                          setDoctorId(v)
                          setAppointmentDate('')
                        }}
                      >
                        <SelectTrigger className={COMPACT_FIELD}>
                          <SelectValue placeholder="Seleccionar doctor" />
                        </SelectTrigger>
                        <SelectContent>
                          {doctors.map((doc) => (
                            <SelectItem key={doc.id} value={doc.id}>
                              <span className="flex items-center gap-2">
                                <DoctorAvatar gender={guessGender(doc.first_name)} size={20} />
                                <span>
                                  {doc.first_name} {doc.last_name}
                                  {doc.department ? ` — ${doc.department}` : ''}
                                </span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : selectedDoctor ? (
                      <div className="flex h-9 items-center gap-2 rounded-md border border-input bg-ml-surface-container-low px-2.5">
                        <DoctorAvatar gender={selectedDoctorGender} size={22} />
                        <span className="truncate text-[13px] font-medium text-ml-on-surface">
                          {selectedDoctorGender === 'female' ? 'Dra.' : 'Dr.'} {selectedDoctor.first_name} {selectedDoctor.last_name}
                        </span>
                      </div>
                    ) : (
                      <div className="flex h-9 items-center rounded-md border border-input px-2.5 text-[13px] text-muted-foreground">
                        Sin doctores disponibles
                      </div>
                    )}
                  </div>

                  {/* Fecha + Horario side by side */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <FieldLabel>Fecha *</FieldLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            disabled={slotsLoading || availableDates.length === 0}
                            className={cn(
                              'w-full justify-start px-2.5 font-normal',
                              COMPACT_FIELD,
                              attemptedSubmit && errors.date && 'border-red-400',
                            )}
                          >
                            <CalendarIcon className="mr-1.5 h-4 w-4 shrink-0" />
                            <span className="truncate">
                              {selectedDate
                                ? selectedDateLabel
                                : slotsLoading
                                  ? 'Cargando...'
                                  : 'Fecha'}
                            </span>
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            style={calendarAccent}
                            selected={selectedDate ? parseYmd(selectedDate) : undefined}
                            onSelect={(date) => {
                              if (!date) return
                              setSelectedDate(toYmd(date))
                              setAppointmentDate('')
                            }}
                            disabled={(date) => !availableDateSet.has(toYmd(date))}
                            defaultMonth={
                              selectedDate
                                ? parseYmd(selectedDate)
                                : availableDates[0]
                                  ? parseYmd(availableDates[0].date)
                                  : undefined
                            }
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <FieldLabel>Horario *</FieldLabel>
                      <Select
                        value={appointmentDate}
                        onValueChange={setAppointmentDate}
                        disabled={slotsLoading || !selectedDate || slotsForSelectedDate.length === 0}
                      >
                        <SelectTrigger className={cn(COMPACT_FIELD, attemptedSubmit && errors.time && 'border-red-400')}>
                          <SelectValue placeholder={slotsLoading ? '...' : 'Hora'} />
                        </SelectTrigger>
                        <SelectContent>
                          {slotsForSelectedDate.map((slot) => (
                            <SelectItem key={slot.start_at} value={slot.start_at}>
                              {formatClinicTime(slot.start_at, slotsTimezone)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {attemptedSubmit && (errors.date || errors.time) && (
                    <FieldError>Selecciona fecha y horario.</FieldError>
                  )}

                  {/* Duración + Estado side by side (both read-only) */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <FieldLabel hint="Usa la duración del tratamiento elegido o la duración configurada por el doctor.">Duración</FieldLabel>
                      <div className="flex h-9 items-center gap-1.5 rounded-md border border-input bg-slate-50 px-2.5 text-[13px] text-slate-600">
                        <Clock className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                        <span className="font-medium">{durationMinutes} min</span>
                      </div>
                    </div>
                    <div>
                      <FieldLabel hint="La cita queda por confirmar hasta que el doctor la apruebe.">Estado</FieldLabel>
                      <div className="flex h-9 items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 text-[13px] text-amber-800">
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                        <span className="truncate font-medium">Pendiente</span>
                      </div>
                    </div>
                  </div>

                  {/* Availability status — compact */}
                  {slotsError && (
                    <p className="flex items-center gap-1.5 text-[11px] text-red-600">
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                      {slotsError}
                    </p>
                  )}
                  {noAvailability && (
                    <div className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-800">
                      <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
                      <span>No hay horarios disponibles (hoy al {maxDateLabel}).</span>
                    </div>
                  )}
                  {availableSlots.length > 0 && (
                    <p className="text-[11px] text-muted-foreground">Disponible hasta el {availableUntilLabel}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Compact fixed footer */}
            <div className="flex shrink-0 items-center justify-between border-t border-teal-100 px-6 py-3">
              <button
                type="button"
                onClick={resetAndClose}
                className="text-[13px] text-slate-500 underline-offset-2 hover:text-slate-700 hover:underline"
              >
                Cancelar
              </button>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="h-9 rounded bg-teal-600 px-6 text-white shadow-lg shadow-teal-600/20 hover:bg-teal-700"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Creando...
                      </>
                    ) : (
                      'Agendar Cita'
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[240px]">
                  {summaryReady ? (
                    <div className="space-y-0.5 text-xs">
                      <p><span className="opacity-70">Paciente:</span> {selectedPatient!.first_name} {selectedPatient!.last_name}</p>
                      <p><span className="opacity-70">Fecha:</span> {formatClinicDate(appointmentDate, slotsTimezone)} · {formatClinicTime(appointmentDate, slotsTimezone)}</p>
                      <p><span className="opacity-70">Tipo:</span> {TYPE_LABELS[appointmentType] ?? appointmentType} · {durationMinutes} min</p>
                    </div>
                  ) : (
                    <span className="text-xs">Completa paciente, fecha, horario y motivo.</span>
                  )}
                </TooltipContent>
              </Tooltip>
            </div>
          </form>
        </TooltipProvider>
      </DialogContent>
    </Dialog>
  )
}

// Compact confirmation card shown once a patient is selected, so the user can
// visually confirm they picked the right record (name, age, sex, allergies).
function PatientMiniProfile({ patient, onChange }: { patient: Patient; onChange: () => void }) {
  const allergies = patient.allergies ?? []
  const genderLabel = GENDER_LABELS[patient.gender as Gender] ?? '—'
  return (
    <div className="rounded-md border border-teal-200 bg-teal-50 p-2.5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-teal-100 text-[13px] font-semibold text-teal-700">
            {patient.first_name[0]}{patient.last_name[0]}
          </div>
          <div className="min-w-0">
            <p className="text-[13px] font-medium text-teal-900 truncate leading-tight">
              {patient.first_name} {patient.last_name}
            </p>
            <p className="text-[11px] text-teal-700 leading-tight">
              {typeof patient.age === 'number' ? `${patient.age} años` : 'Edad n/d'} · {genderLabel}
              {patient.mrn ? ` · ${patient.mrn}` : ''}
            </p>
          </div>
        </div>
        <Button type="button" variant="ghost" size="sm" className="h-7 shrink-0 px-2 text-xs text-teal-700" onClick={onChange}>
          Cambiar
        </Button>
      </div>

      {/* Allergies — the safety-critical detail worth surfacing up front */}
      <div className="mt-1.5 flex flex-wrap items-center gap-1">
        {allergies.length === 0 ? (
          <span className="inline-flex items-center gap-1 text-[10px] text-teal-600">
            <Check className="h-3 w-3" /> Sin alergias registradas
          </span>
        ) : (
          <>
            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-red-600">
              <AlertTriangle className="h-3 w-3" /> Alergias:
            </span>
            {allergies.map((a, i) => (
              <span
                key={`${a.name}-${i}`}
                className={cn(
                  'inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium',
                  ALLERGY_SEVERITY_STYLES[a.severity] ?? 'bg-slate-100 text-slate-700 border-slate-200',
                )}
                title={a.reaction || undefined}
              >
                {a.name}
              </span>
            ))}
          </>
        )}
      </div>
    </div>
  )
}

function FieldError({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-1 flex items-center gap-1 text-[11px] text-red-600">
      <AlertTriangle className="h-3 w-3 shrink-0" />
      {children}
    </p>
  )
}
